import { TreeViewNode } from '../../components/TreeView/TreeView';
import {
  Model,
  TestResult,
  TestRun,
  TreeLeaf,
  TreeNode,
  ApiTreeLeaf,
  ApiTreeNode,
} from '../../types';
import { BuildLeaf, SidebarData, SidebarVariants, TreeViews } from './types';

export const buildApiLeaf: BuildLeaf = (leaf) => ({
  label: (leaf as ApiTreeLeaf).label,
  totals: (leaf as ApiTreeLeaf).totals,
  assetIds: (leaf as ApiTreeLeaf).asset_ids,
});

export const getTreeNodeAssetIds = (
  node: Pick<TreeViewNode, 'id' | 'assetIds'>,
): string[] => node.assetIds ?? [node.id];

export const getAllSubNodes = (
  nodes: TreeViewNode[] | TreeViews,
): TreeViewNode[] => {
  const subNodes = nodes
    .map((node) => {
      if (node.children) {
        return getAllSubNodes(node.children);
      }
      return [];
    })
    .flat();
  return [...nodes, ...subNodes];
};

const getNotEmptyNodes = (arr: TreeViewNode[]): TreeViewNode[] | boolean =>
  arr.filter((i) => {
    if (i && !i.children) return true;

    if (i?.children?.length) return getNotEmptyNodes(i.children);

    return false;
  });

const nameComparer = (a: TreeViewNode, b: TreeViewNode) => {
  if (a.id.includes('folderNode') && !b.id.includes('folderNode')) {
    return -1;
  }
  if (b.id.includes('folderNode') && !a.id.includes('folderNode')) {
    return 1;
  }
  return a.name.localeCompare(b.name);
};

export const getItemLabel = (
  item: TreeLeaf,
  modelsData?: Record<string, Model>,
  testsData?: Record<string, TestResult[]> | Record<string, TestRun[]>,
  useTableName: boolean = false,
) => {
  const isTest = item.resource_type === 'test';

  if (isTest) {
    const label = testsData?.[item.node_id]?.[0]?.metadata.test_name;
    if (label) return label;
  }

  if (!isTest) {
    const model = modelsData?.[item.node_id];
    let label;
    if (model?.artifact_type === 'exposure') {
      label = model?.label || model?.name;
    } else if (useTableName) {
      label = model?.table_name;
    } else {
      label = model?.name;
    }
    if (label) return label;
  }

  const split = item.node_id.split('.');
  return split[split.length - 1];
};

const filterNode = (
  node: TreeNode | TreeLeaf[],
  predicate: (leaf: TreeLeaf) => boolean,
): TreeNode | TreeLeaf[] => {
  const isFolder = (n: TreeNode | TreeLeaf[]) =>
    typeof n === 'object' && !Array.isArray(n);

  if (Array.isArray(node)) {
    return node.filter(predicate);
  }

  const filteredNode: TreeNode = {};
  Object.keys(node).forEach((key) => {
    if (node[key] == null) return;
    const filteredChild = filterNode(node[key], predicate);
    if (
      (Array.isArray(filteredChild) && filteredChild.length > 0) ||
      (isFolder(filteredChild) && Object.keys(filteredChild).length > 0)
    ) {
      filteredNode[key] = filteredChild;
    }
  });

  return filteredNode;
};

export const filterTree = (
  node: SidebarData,
  predicate: (leaf: TreeLeaf) => boolean,
) => {
  if (!node) return null;
  return filterNode(node, predicate);
};

export const excludeExposures = (root: SidebarData) =>
  filterTree(root, (leaf) => leaf.resource_type !== 'exposure');

export const excludeTestsAndExposures = (root: SidebarData) =>
  filterTree(
    root,
    (leaf) =>
      leaf.resource_type !== 'test' && leaf.resource_type !== 'exposure',
  );

export const includeModelsAndSources = (root: SidebarData) =>
  filterTree(
    root,
    (leaf) => leaf.resource_type === 'model' || leaf.resource_type === 'source',
  );

export const includeModelsSourcesTablesAndViews = (root: SidebarData) =>
  filterTree(root, (leaf) =>
    ['model', 'source', 'table', 'view'].includes(leaf.resource_type || ''),
  );

export const includeModelsOnly = (root: SidebarData) =>
  filterTree(root, (leaf) => leaf.resource_type === 'model');

export const includeModelsAndSnapshots = (root: SidebarData) =>
  filterTree(
    root,
    (leaf) =>
      leaf.resource_type === 'model' || leaf.resource_type === 'snapshot',
  );

export const formatTreeData = (
  id: string,
  name: string,
  children: TreeLeaf[] | ApiTreeLeaf[] | TreeNode | ApiTreeNode,
  searchValue: string,
  buildLeaf: BuildLeaf,
  nodeLevel: number = 0,
  pathSeparator: string = '/',
  folderTotalsPath?: string,
): TreeViewNode => {
  const formattedChildren: TreeViewNode[] = [];

  const currentFolderPath =
    folderTotalsPath === undefined
      ? name
      : `${folderTotalsPath}${pathSeparator}${name}`;

  const parseFilesArray = (
    files: (TreeLeaf | ApiTreeLeaf)[],
    parentId: string,
    parentName: string,
  ) => {
    files.forEach((file) => {
      const { label, totals, assetIds } = buildLeaf(file);

      const isSearchMatch =
        label?.toLowerCase().includes(searchValue.toLowerCase()) ||
        parentName?.toLowerCase().includes(searchValue.toLowerCase());

      const leafTotalsType = file.resource_type === 'test' ? 'test' : 'asset';

      if (isSearchMatch) {
        formattedChildren.push({
          id: file.node_id,
          assetIds,
          parentId,
          name: label,
          totals,
          nodeLevel: nodeLevel + 1,
          resourceType: file.resource_type,
          totalsType: leafTotalsType,
          totalsRequestKey: file.node_id,
        });
      }
    });
  };

  if (Array.isArray(children)) {
    parseFilesArray(children, id, name);
  } else {
    Object.keys(children).forEach((key) => {
      const files = children[key];
      if (Array.isArray(files)) {
        parseFilesArray(files, id, name);
        return;
      }

      formattedChildren.push(
        formatTreeData(
          `${id}/folderNode_${key}`,
          key,
          children[key],
          searchValue,
          buildLeaf,
          nodeLevel + 1,
          pathSeparator,
          currentFolderPath,
        ),
      );
    });
  }

  const nonEmptyNodes = getNotEmptyNodes(formattedChildren);

  return {
    id,
    name,
    children: Array.isArray(nonEmptyNodes)
      ? nonEmptyNodes.sort(nameComparer)
      : formattedChildren?.sort(nameComparer),
    nodeLevel,
    resourceType: 'folder',
    totalsType: 'folder',
    totalsRequestKey: currentFolderPath,
  };
};

export const getFolderNodeIds = (
  tree: TreeViewNode | TreeViewNode[],
): string[] => {
  const folderIds: string[] = [];

  if (Array.isArray(tree)) {
    tree?.forEach((node) => {
      if (node.id.includes('folderNode') && node.children?.length) {
        folderIds.push(node.id);
        folderIds.push(...getFolderNodeIds(node.children));
      }
    });
  } else {
    if (tree.children?.length) {
      folderIds.push(tree.id);
      folderIds.push(...getFolderNodeIds(tree.children));
    }
  }

  return folderIds;
};

export const collectLeafIds = (node: TreeNode | TreeLeaf[]): string[] => {
  if (Array.isArray(node)) {
    return node.map((leaf) => leaf.node_id);
  }

  const leafIds: string[] = [];
  Object.values(node).forEach((child) => {
    if (child != null) {
      leafIds.push(...collectLeafIds(child));
    }
  });
  return leafIds;
};

export const selectSidebarData = (
  pathname: string,
  variants: SidebarVariants,
  treeViewData: SidebarData,
): SidebarData => {
  if (pathname.includes('/report/lineage')) {
    return variants.noTestsNoEphemeral;
  }

  if (
    pathname.includes('/report/test-results') ||
    pathname.includes('/report/test-runs') ||
    pathname.includes('/test-management') ||
    pathname.includes('/incidents')
  ) {
    return variants.noExposures;
  }

  if (pathname.includes('/report/dashboard')) {
    // Currently not showing tests in the sidebar for the dashboard
    // a - because the BE does not support filtering dashboard data by tests
    // b - the dashboard displays data on both assets and tests, having it filtered on only tests will look odd with the current UI
    return variants.noTestsNoExposures;
  }

  if (pathname.includes('/data-health')) {
    return variants.noTestsNoExposures;
  }

  if (pathname.includes('/report/model-runs')) {
    return variants.modelsAndSnapshots;
  }

  if (pathname.includes('/catalog')) {
    return treeViewData;
  }

  if (pathname.includes('/metrics')) {
    return variants.modelsSourcesTablesAndViews;
  }

  return variants.modelsAndSources;
};
